import { t } from 'i18next';
import React, {
  useEffect,
  useState,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import { GetAffectation } from 'services/affectationService';

import { AffectationType } from 'components/types';
import { useParams } from 'next/navigation';
import UserShortCut from 'components/common/UserShortCut';
import { useFormikContext } from 'formik';
import Search from './Search';

function UsersBox({
  url,
  ids,
  setIds,
  isBox = true,
  height,
  length = 5,
  withoutFormik = false,
  showCount = true,
  hideIds = [],
  setIsSelectAll,
  usePathID,
  firstCurrentList,
}: {
  url: string;
  ids: string[];
  setIds: Dispatch<SetStateAction<string[]>>;
  isBox?: boolean;
  height?: number;
  length?: number;
  withoutFormik?: boolean;
  showCount?: boolean;
  hideIds?: string[];
  setIsSelectAll?: Dispatch<SetStateAction<boolean>>;
  usePathID?: boolean;
  firstCurrentList?: boolean;
}) {
  const { id } = useParams();
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<AffectationType | null>(null);
  const formik = !withoutFormik ? useFormikContext() : null;
  const [searchValue, setSearchValue] = useState('');
  const [currentList, setCurrentList] = useState(firstCurrentList ? 1 : 0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const listRef = useRef<HTMLUListElement>(null);
  const prevSearchRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const isIncrementingRef = useRef(false); 

  useEffect(() => {
    if (!hasMoreData && currentList > 0) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    
    const fetchAffectation = async () => {
      try {
        setIsLoadingMore(true);
        
        const filters = {
          searchValue,
          lenght: length,
          currentList,
          id: usePathID ? (id as string) : 0,
          roleName: '',
          userid: '',
          roleId: '',
          ids: [],
          selectAll: false,
          isGroup: true,
        };

        const response = await GetAffectation(url, filters);
        
        const isNewSearch = searchValue !== prevSearchRef.current;
        prevSearchRef.current = searchValue;
        
        if (!response || response.length === 0) {
          setHasMoreData(false);
          if (isNewSearch) {
            setData([]);
          }
          return;
        }

        if (isNewSearch) {
          setHasMoreData(true);
        }
        
        setData((prev) => {
          if (isNewSearch || !prev) {
            return response;
          }
          
          const existingIds = new Set(prev.map(user => user.value));
          const newUsers = response.filter(user => !existingIds.has(user.value));
          
          if (response.length === 0) {
            setHasMoreData(false);
          }
          
          return [...prev, ...newUsers];
        });
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching users:', error);
        }
      } finally {
        setIsLoadingMore(false);
        isIncrementingRef.current = false; 
      }
    };
    
    fetchAffectation();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchValue, currentList, url, length, usePathID, id, hasMoreData]);

  const visibleUsers = React.useMemo(() => {
    if (!data) return [];
    
    const filtered = data.filter((user) => !hideIds.includes(user.value));
    const uniqueMap = new Map(filtered.map(user => [user.value, user]));
    
    return Array.from(uniqueMap.values());
  }, [data, hideIds]);

  const selectedVisibleCount = visibleUsers.filter((user) =>
    ids.includes(user.value),
  ).length;

  const isAllSelected =
    visibleUsers.length > 0 && selectedVisibleCount === visibleUsers.length;
  const isSomeSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visibleUsers.length;
  const isAnySelected = selectedVisibleCount > 0;

  const incrementCurrentList = () => {
    if (isIncrementingRef.current || isLoadingMore || !hasMoreData) {
      return;
    }
    
    isIncrementingRef.current = true;
    setCurrentList((prev) => prev + 1);
  };

  useEffect(() => {
    const fetchMoreIfNeeded = () => {
      if (
        data &&
        data.length > 0 &&
        !isLoadingMore &&
        !isIncrementingRef.current &&
        hasMoreData &&
        visibleUsers.length < length &&
        visibleUsers.length < data.length
      ) {
        incrementCurrentList();
      }
    };

    fetchMoreIfNeeded();
  }, [data, visibleUsers.length, length, isLoadingMore, hasMoreData]);

  const handleScroll = () => {
    if (listRef.current && !isLoadingMore && !isIncrementingRef.current && hasMoreData) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        incrementCurrentList();
      }
    }
  };

  useEffect(() => {
    const currentListRef = listRef.current;
    if (currentListRef) {
      currentListRef.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (currentListRef) {
        currentListRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isLoadingMore, hasMoreData]); 

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSomeSelected;
      selectAllRef.current.setAttribute(
        'data-indeterminate',
        isSomeSelected.toString(),
      );
    }
  }, [isSomeSelected]);

  const handleSearch = (e: string) => {
    setSearchValue(e);
    setCurrentList(0);
    setHasMoreData(true);
    isIncrementingRef.current = false; 
  };

  const handleSelectUser = (user) => {
    const updatedIds = ids.includes(user.value)
      ? ids.filter((id: string) => id !== user.value)
      : [...ids, user.value];

    setIds(updatedIds);

    if (!withoutFormik && formik) {
      const currentList = formik.values.usersNotificationList || [];

      formik.setFieldValue(
        'usersNotificationList',
        currentList.some((u) => u.value === user.value)
          ? currentList.filter((u) => u.value !== user.value)
          : [...currentList, user],
      );

      formik.setFieldValue('usersNotification', updatedIds);
    }
  };

  const handleToggleAll = () => {
    if (isAnySelected) {
      setIds([]);

      if (!withoutFormik && formik) {
        formik.setFieldValue('usersNotificationList', []);
        formik.setFieldValue('usersNotification', []);
      }

      setIsSelectAll?.(false);
    } else {
      const allIds = visibleUsers.map((u) => u.value);
      setIds(allIds);

      if (!withoutFormik && formik) {
        formik.setFieldValue('usersNotificationList', visibleUsers);
        formik.setFieldValue('usersNotification', allIds);
      }

      setIsSelectAll?.(true);
    }
  };

  return (
    <>
      {data && (
        <div
          className={` ${isBox ? 'minthr-border minthr-p-2 minthr-bg-white minthr-box-round' : ''}`}
        >
          <div className="minthr-flex minthr-flex-items-center  minthr-mb-2">
            <Search
              onSearch={handleSearch}
              placeholder={t('Search')}
              className="minthr-searchbar-s w-100"
              inputClassName="list-search w-100"
            />
          </div>

          {showCount && (
            <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-mb-3">
              <h5 className="minthr-color-dark">
                {t('approval.users')}{' '}
                <span className="minthr-font-weight-600 total-selected">
                  {visibleUsers.length}
                </span>
              </h5>

              <div className="minthr-flex minthr-flex-column">
                <div className="minthr-chackbox">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    name="selectAll"
                    id="selectAll"
                    checked={isAllSelected}
                    onChange={handleToggleAll}
                  />
                  <label
                    htmlFor="selectAll"
                    className="minthr-ml-2 cursor-pointer"
                  >
                    {t('constent.selectAll')}
                  </label>
                </div>
              </div>
            </div>
          )}

          <ul
            ref={listRef}
            className="minthr-p-0 minthr-m-0 minthr-overflow-auto"
            id="ListScrole"
            style={{
              height: ` calc(var(--unit-minthr) * ${height ? height : 145} )`,
              listStyle: 'none',
            }}
          >
            {visibleUsers.length > 0 ? (
              visibleUsers.map((user) => {
                return (
                  <li key={user.value}>
                    <div className="dropdown-item minthr-flex minthr-flex-items-center minthr-bg-white minthr-py-1 minthr-pr-3 minthr-pl-0 minthr-border-b minthr-border-light-gray minthr-border-rounded-0 notifie-user-item cursor-default">
                      <UserShortCut
                        className="minthr-avatar minthr-avatar-m minthr-mr-2"
                        src={`upload/profileimages/${user.value}/${user.imagePath}`}
                        alt=""
                      />
                      <div className="w-50 cursor-default">
                        <h5 className="minthr-text-truncate minthr-color-dark">
                          {user.text}
                        </h5>
                      </div>

                      <div className="minthr-chackbox minthr-ml-auto minthr-mr-1 minthr-pr-3 minthr-pb-0">
                        <input
                          name="type"
                          type="checkbox"
                          id={`user-checkbox-${user.value}`}
                          className="checkbox-item"
                          checked={
                            !withoutFormik
                              ? formik?.values.usersNotificationList
                                ? (formik?.values.usersNotificationList as any[]).some(
                                    (item) => item.value === user.value,
                                  )
                                : ids.includes(user.value)
                              : ids.includes(user.value)
                          }
                          onChange={() => {}}
                        />
                        <label
                          className="minthr-form-label minthr-text-hide minthr-p-0"
                          htmlFor={`user-checkbox-${user.value}`}
                          onClick={() => handleSelectUser(user)}
                        >
                          .
                        </label>
                      </div>
                    </div>
                  </li>
                );
              })
            ) : (
              <div className="minthr-h-100 minthr-flex minthr-flex-items-center minthr-flex-justify-center">
                <p className="minthr-color-gray">{t('NoData.Empty')}</p>
              </div>
            )}
          </ul>
        </div>
      )}
    </>
  );
}

export default UsersBox;